import React, { useRef, useEffect } from 'react';
import type { ChartData } from '../../types/analytics';

interface Props {
  data: ChartData;
  title: string;
  height?: number;
  horizontal?: boolean;
  showValues?: boolean;
}

export default function BarChart({ 
  data, 
  title, 
  height = 300, 
  horizontal = false, 
  showValues = true 
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !data.datasets.length) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, height);

    const padding = 40;
    const chartWidth = rect.width - padding * 2;
    const chartHeight = height - padding * 2;

    const dataset = data.datasets[0];
    const values = dataset.data;
    const maxValue = Math.max(...values, 0);

    if (horizontal) {
      // Horizontal bar chart
      const barHeight = chartHeight / values.length;
      const barPadding = barHeight * 0.2;
      const actualBarHeight = barHeight - barPadding;

      values.forEach((value, index) => {
        const barWidth = (value / maxValue) * chartWidth;
        const y = padding + index * barHeight + barPadding / 2;

        // Draw bar
        ctx.fillStyle = dataset.backgroundColor || 'rgba(0, 48, 73, 0.8)';
        ctx.fillRect(padding, y, barWidth, actualBarHeight);

        // Draw value
        if (showValues) {
          ctx.fillStyle = 'white';
          ctx.font = '12px sans-serif';
          ctx.textAlign = 'left';
          ctx.fillText(value.toString(), padding + barWidth + 5, y + actualBarHeight / 2 + 4);
        }

        // Draw label
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.textAlign = 'right';
        ctx.fillText(data.labels[index], padding - 10, y + actualBarHeight / 2 + 4);
      });
    } else {
      // Vertical bar chart
      const barWidth = chartWidth / values.length;
      const barPadding = barWidth * 0.2;
      const actualBarWidth = barWidth - barPadding;

      values.forEach((value, index) => {
        const barHeight = (value / maxValue) * chartHeight;
        const x = padding + index * barWidth + barPadding / 2;
        const y = padding + chartHeight - barHeight;

        // Draw bar
        ctx.fillStyle = dataset.backgroundColor || 'rgba(0, 48, 73, 0.8)';
        ctx.fillRect(x, y, actualBarWidth, barHeight);

        // Draw value
        if (showValues) {
          ctx.fillStyle = 'white';
          ctx.font = '12px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(value.toString(), x + actualBarWidth / 2, y - 5);
        }

        // Draw label
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.textAlign = 'center';
        ctx.fillText(data.labels[index], x + actualBarWidth / 2, height - 10);
      });
    }

  }, [data, height, horizontal, showValues]);

  return (
    <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
      <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full"
          style={{ height: `${height}px` }}
        />
      </div>
    </div>
  );
}